import { useTranslation } from 'react-i18next';
import {
  Package, AlertTriangle,
} from 'lucide-react';
import { cn } from '@/lib/classnames';
import type {
  StockItem, StockStatus 
} from '../../types';
import { getStockStatus } from '../../types';

interface StockLevelCardProps {
  readonly item: StockItem;
  readonly onClick?: () => void;
}

const statusStyles = {
  'in-stock': 'border-green-200 bg-green-50',
  'low-stock': 'border-amber-200 bg-amber-50',
  'out-of-stock': 'border-red-200 bg-red-50',
} as const;

const statusBadgeStyles = {
  'in-stock': 'bg-green-100 text-green-800',
  'low-stock': 'bg-amber-100 text-amber-800',
  'out-of-stock': 'bg-red-100 text-red-800',
} as const;

const statusTranslationKeys: Record<StockStatus, string> = {
  'in-stock': 'inStock',
  'low-stock': 'lowStock',
  'out-of-stock': 'outOfStock',
};

/**
 * Card component displaying stock level for a single inventory item.
 */
export function StockLevelCard({
  item, onClick,
}: StockLevelCardProps) {
  const { t } = useTranslation('inventory');
  const status = getStockStatus(item);
  const translationKey = statusTranslationKeys[status];

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full rounded-lg border p-4 text-left transition-shadow hover:shadow-md',
        statusStyles[status]
      )}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-md bg-white p-2 shadow-sm">
            <Package className="h-5 w-5 text-gray-600" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">{item.itemName}</h3>
            <p className="text-sm text-gray-500">{item.sku}</p>
          </div>
        </div>

        <span
          className={cn(
            'inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium',
            statusBadgeStyles[status]
          )}
        >
          {status === 'low-stock' && <AlertTriangle className="h-3 w-3" />}
          {t(`stockLevel.${translationKey}`)}
        </span>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
        <div>
          <p className="text-gray-500">Quantity</p>
          <p className="font-semibold text-gray-900">
            {item.currentQuantity} {item.unit}
          </p>
        </div>
        <div>
          <p className="text-gray-500">Reorder Point</p>
          <p className="font-semibold text-gray-900">
            {item.reorderPoint} {item.unit}
          </p>
        </div>
        <div>
          <p className="text-gray-500">Category</p>
          <p className="font-semibold text-gray-900">{item.category}</p>
        </div>
        <div>
          <p className="text-gray-500">Location</p>
          <p className="font-semibold text-gray-900">{item.storageLocationName}</p>
        </div>
      </div>
    </button>
  );
}
